import { ProductWarehouseStockItem } from "@api/productApi";
import { Product } from "@api/throughShipment/onlineOrderApi";
import PopupBackground from "@component/PopupBackground";
import { storageTypeOptions, RentrapStorageKey, TcatStorageKey } from "@constant/StorageType";
import { WarehouseCode } from "@constant/WarehouseCodeValue";
import useIntRouteParam from "@hooks/useIntRouteParam";
import {
  onlineOrderState,
  fetchCreateProduct,
  fetchUpdateProduct,
  fetchProductDetailBySku,
  fetchProductStockBySku,
} from "@redux/onlineOrderSlice";
import getAvailableExpiryTypeOptions from "@utils/getAvailableExpiryTypeOptions";

import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Row, Spin } from "antd";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import CommonFormFields from "./components/CommonFormFields";
import RentrapWarehouseFields from "./components/RentrapWarehouseFields";
import TcatWarehouseFields from "./components/TcatWarehouseFields";

const Wrapper = styled(Form)`
  position: relative;
  min-width: 650px;
  padding: 30px;
  background: ${({ theme }) => theme.colorNeutral100};
  overflow: auto;
  .ant-form-item-label {
    max-width: 131px;
    min-width: 131px;
    text-align: left;
  }
  .ant-form-item-required::before {
    order: 1;
  }
  .ant-form-item-required::after {
    display: none;
  }
  .ant-input {
    width: 230px;
  }
  .ant-form-item {
    margin-bottom: 5px;
  }
  .ant-select {
    width: 230px;
  }
`;

type Props = {
  onClose: () => void;
  currentProduct?: Product;
  warehouseCode: string;
  isB2B: boolean;
  warehouseIsTcat: boolean;
};

export default function EditProductPopup(props: Props) {
  const { onClose, currentProduct, warehouseCode, isB2B, warehouseIsTcat } = props;
  const dispatch = useDispatch();
  const orderId = useIntRouteParam("orderId", true);
  const { productBarcodeInfo, productStockInfo, isProductInfoFetching } = useSelector(onlineOrderState);

  const [form] = Form.useForm();
  const storageType = Form.useWatch("storageType", form);
  const effectiveDateFormValue = Form.useWatch("effectiveDate", form);
  const skuFormValue: string = Form.useWatch("sku", form);
  const storageFormValue = Form.useWatch("storageType", form);
  const expiryTypeFormValue: boolean = Form.useWatch("expiryType", form);

  const [error, setError] = useState<string>("");
  const [stockInfoList, setStockInfoList] = useState<ProductWarehouseStockItem[]>([]);
  const [stocksForEffectiveDate, setStocksForEffectiveDate] = useState<ProductWarehouseStockItem[]>([]);
  const [sameStorageTypeStocks, setSameStorageTypeStocks] = useState<ProductWarehouseStockItem[]>([]);
  const [stocksForBatch, setStocksForBatch] = useState<ProductWarehouseStockItem[]>([]);
  const [availableQty, setAvailableQty] = useState<number>(0);

  const availableStorageTypes = new Set(stockInfoList.map((stock) => stock.storageType));
  const matchingStorage = storageTypeOptions.filter((option) => availableStorageTypes.has(option.key));

  const expiryTypeOptions = useMemo(
    () =>
      getAvailableExpiryTypeOptions(stockInfoList.filter((stock) => stock.storageType === storageFormValue)),
    [stockInfoList, storageFormValue],
  );

  const handleSubmit = (formValue: any) => {
    let effectiveDate = "";
    if (formValue.effectiveDate) {
      effectiveDate = warehouseIsTcat
        ? moment(formValue.effectiveDate).format("YYYY-MM-DD")
        : JSON.parse(formValue.effectiveDate) || "";
    }

    if (currentProduct) {
      // 編輯
      const params = {
        orderId,
        productId: currentProduct.id,
        sku: formValue.sku,
        storageType: warehouseCode === WarehouseCode.REN_TRAP ? formValue.storageType : TcatStorageKey.GOOD,
        estimatedQty: Number(formValue.estimatedQty),
        unitPrice: Number(formValue.unitPrice),
        batch: formValue.batch || null,
        effectiveDate,
      };

      dispatch(fetchUpdateProduct(params));
    } else {
      // 新增
      const params = {
        orderId,
        sku: formValue.sku,
        storageType: warehouseCode === WarehouseCode.REN_TRAP ? formValue.storageType : TcatStorageKey.GOOD,
        estimatedQty: Number(formValue.estimatedQty),
        unitPrice: formValue.unitPrice ? Number(formValue.unitPrice) : null,
        batch: formValue.batch || null,
        effectiveDate,
      };
      dispatch(fetchCreateProduct(params));
    }
    onClose();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (skuFormValue && warehouseCode) {
        dispatch(fetchProductDetailBySku({ sku: skuFormValue, warehouse: warehouseCode }));
        dispatch(
          fetchProductStockBySku({
            sku: skuFormValue,
            warehouse: warehouseCode,
          }),
        );
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [dispatch, skuFormValue, warehouseCode]);

  useEffect(() => {
    const productInfo = productBarcodeInfo?.results[0];
    if (productInfo) {
      setError("");
      form.setFieldsValue({
        standardPrice: 0,
        barcode: productInfo.barcode,
        productName: productInfo.productName,
        weightedCost: productInfo.weightedCost,
      });
    } else {
      setError("查無對應商品，請確認商品品號是否存在");
      form.resetFields();
    }
  }, [productBarcodeInfo, form]);

  useEffect(() => {
    if (productStockInfo.length === 0) return;
    const stockInfo = productStockInfo[0]?.stockInfo ?? [];
    setStockInfoList(stockInfo);

    if (currentProduct) {
      const matchedStock = stockInfo.find(
        (item) =>
          item.effectiveDate === (currentProduct.effectiveDate || null) &&
          item.batch === (currentProduct.batch || null),
      );
      form.setFieldsValue({ expiryType: matchedStock?.isExpiring ?? false });
    }

    if (warehouseIsTcat) {
      const currentStock = stockInfo[0]?.canSalesQty || 0;
      setAvailableQty(currentStock);
      form.setFieldValue("stock", currentStock);
      return;
    }

    if (!skuFormValue || !productBarcodeInfo?.results[0]) return;
    if (stockInfo.length > 0) {
      setError("");
      if (!currentProduct) {
        const hasGoodStock = stockInfo.some((item) => item.storageType === RentrapStorageKey.GOOD);
        form.setFieldsValue({ storageType: hasGoodStock ? RentrapStorageKey.GOOD : undefined });
      }
    } else {
      setError("此商品無庫存");
      form.setFieldsValue({ storageType: undefined, effectiveDate: undefined, batch: undefined, stock: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productStockInfo, warehouseIsTcat, productBarcodeInfo, skuFormValue, currentProduct]);

  useEffect(() => {
    if (storageFormValue) {
      const filteredStock = stockInfoList.filter((stockInfo) => {
        return (
          stockInfo.storageType === storageFormValue && (stockInfo.isExpiring ?? false) === (expiryTypeFormValue ?? false)
        );
      });
      setSameStorageTypeStocks(filteredStock);
    }
  }, [stockInfoList, storageFormValue, expiryTypeFormValue]);

  useEffect(() => {
    if (expiryTypeOptions.length === 0) return;
    const isCurrentValueValid = expiryTypeOptions.some((option) => option.value === (expiryTypeFormValue ?? false));
    if (!isCurrentValueValid) {
      form.setFieldsValue({
        expiryType: expiryTypeOptions[0].value,
        effectiveDate: undefined,
        batch: undefined,
        stock: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiryTypeOptions]);

  useEffect(() => {
    const stockMap = new Map();
    const stocks = sameStorageTypeStocks.filter((item) => {
      if (!stockMap.has(item.effectiveDate)) {
        stockMap.set(item.effectiveDate, true);
        return true;
      }
      return false;
    });
    setStocksForEffectiveDate(stocks);
  }, [sameStorageTypeStocks]);

  useEffect(() => {
    if (effectiveDateFormValue) {
      const stockMap = new Map();
      const stocks = sameStorageTypeStocks.filter((item) => {
        if (!stockMap.has(item.batch)) {
          stockMap.set(item.batch, true);
          return true;
        }
        return false;
      });
      setStocksForBatch(stocks);
    }
  }, [effectiveDateFormValue, sameStorageTypeStocks]);
  const handleBatchChange = (batch: string) => {
    const filteredStockInfo = stocksForBatch.filter((item) => {
      return item.batch === batch;
    });
    form.setFieldsValue({
      stock: filteredStockInfo[0].canSalesQty,
    });
  };

  useEffect(() => {
    if (currentProduct) {
      let editEffectiveDate = null;
      if (warehouseIsTcat) {
        editEffectiveDate = currentProduct.effectiveDate ? moment(currentProduct.effectiveDate) : null;
      } else {
        editEffectiveDate =
          currentProduct.effectiveDate !== undefined ? JSON.stringify(currentProduct.effectiveDate) : undefined;
      }
      form.setFieldsValue({
        ...currentProduct,
        effectiveDate: editEffectiveDate,
      });
    } else {
      form.resetFields();
    }
  }, [currentProduct, warehouseIsTcat, form]);

  return (
    <PopupBackground close={onClose} fixed>
      <Wrapper
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 8 }}
        labelAlign="left"
        layout="horizontal"
        colon={false}
        onFinish={handleSubmit}
        initialValues={{ standardPrice: 0 }}
      >
        <Spin spinning={isProductInfoFetching}>
          <CommonFormFields error={error} />
          {warehouseIsTcat ? (
            <TcatWarehouseFields currentProduct={currentProduct} availableQty={availableQty} isB2B={isB2B} />
          ) : (
            <RentrapWarehouseFields
              form={form}
              storageType={storageType}
              effectiveDateFormValue={effectiveDateFormValue}
              matchingStorage={matchingStorage}
              expiryTypeOptions={expiryTypeOptions}
              stocksForEffectiveDate={stocksForEffectiveDate}
              stocksForBatch={stocksForBatch}
              handleBatchChange={handleBatchChange}
              isB2B={isB2B}
            />
          )}
          <Row justify="end" align="middle">
            <Button type="default" style={{ marginRight: 10 }} onClick={onClose}>
              取消
            </Button>
            <Button type="primary" htmlType="submit">
              確定
            </Button>
          </Row>
        </Spin>
      </Wrapper>
    </PopupBackground>
  );
}
